import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Stack,
  InputAdornment,
  MenuItem, 
  Grid,
  Autocomplete,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  Paper,
  IconButton,
  FormHelperText,
  Divider,
  FormGroup,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { 
  WorkerAssignment, 
  AvailableDesign,
  ActiveWorker 
} from '../types/workerAssignment';
import SearchIcon from '@mui/icons-material/Search';
import api from '../services/api';
import { SelectChangeEvent } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ViewListIcon from '@mui/icons-material/ViewList';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import BuildIcon from '@mui/icons-material/Build';
import ReceivesListDialog from '../components/ReceivesListDialog';
import DesignPreviewDialog from '../components/DesignPreviewDialog';
import { DesignCutting as DesignCuttingType } from '../types/designCutting';
import { makeSafeGridColumns, safeValueGetter, safeValueFormatter } from '../utils/dataGridHelpers';
import { SafeGridValueGetterParams, SafeGridValueFormatterParams, SafeGridRenderCellParams } from '../types/dataGrid';
import PreviewIcon from '@mui/icons-material/Preview';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import BulkAssignmentDialog from '../components/BulkAssignmentDialog';
import BulkReceiveDialog from '../components/BulkReceiveDialog';

const initialFormData = {
  workerId: '',
  design_no: '',
  jobType: 'STITCHING',
  size: '',
  quantity: '',
  issueDate: new Date().toISOString().split('T')[0],
  stitching_rate: '',
  image_url: '',
  garment_part: '',
};

interface ReceiveFormData {
  receive_date: string;
  quantity_received: number;
  defect_quantity: string | number;
  notes: string;
}

const initialReceiveFormData: ReceiveFormData = {
  receive_date: new Date().toISOString().split('T')[0],
  quantity_received: 0,
  defect_quantity: '',
  notes: '',
};

export default function WorkerAssignmentPage() {
  const [assignments, setAssignments] = useState<WorkerAssignment[]>([]);
  const [availableDesigns, setAvailableDesigns] = useState<AvailableDesign[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [receiveFormData, setReceiveFormData] = useState<ReceiveFormData>(initialReceiveFormData);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredAssignments, setFilteredAssignments] = useState<WorkerAssignment[]>([]);
  const [filterNoRate, setFilterNoRate] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [availableWorkers, setAvailableWorkers] = useState<ActiveWorker[]>([]);
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [receiveFormErrors, setReceiveFormErrors] = useState<{[key: string]: string}>({});
  const [rateEditOpen, setRateEditOpen] = useState(false);
  const [rateFormData, setRateFormData] = useState<{stitching_rate: string}>({stitching_rate: ''});
  const [receivesListOpen, setReceivesListOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<WorkerAssignment | null>(null);
  
  // Design preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedPreviewDesignNo, setSelectedPreviewDesignNo] = useState('');
  const [selectedPreviewTopDesign, setSelectedPreviewTopDesign] = useState<DesignCuttingType | undefined>(undefined);
  const [selectedPreviewBottomDesign, setSelectedPreviewBottomDesign] = useState<DesignCuttingType | undefined>(undefined);
  const [allDesigns, setAllDesigns] = useState<DesignCuttingType[]>([]);

  // Add new state for delayed filter
  const [filterDelayed, setFilterDelayed] = useState(false);
  
  // Add new state for pending-only filter (enabled by default)
  const [filterPendingOnly, setFilterPendingOnly] = useState(true);
  
  // Add new state for mobile summary visibility
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  // Bulk assignment dialog
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkReceiveOpen, setBulkReceiveOpen] = useState(false);
  
  // Add function to toggle summary on mobile
  const toggleSummary = () => {
    setSummaryExpanded(prev => !prev);
  };

  // Add function to fetch all designs
  const fetchAllDesigns = async () => {
    try {
      const response = await api.get('/design-cutting');
      if (response.data && Array.isArray(response.data)) {
        setAllDesigns(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch all designs:', error);
    }
  };

  // Normalize image URLs to absolute paths if backend returned relative '/uploads/...'
  const toAbsoluteImageUrl = (url?: string | null): string => {
    try {
      if (!url) return '';
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      const base = (api.defaults.baseURL || '').replace(/\/api\/v1\/?$/, '');
      if (!base) return url;
      return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`;
    } catch {
      return url || '';
    }
  };

  // Add function to handle opening design preview
  const handleOpenDesignPreview = (designNo: string, imageUrl: string) => {
    // Find TOP and BOTTOM designs with the same design number
    const topDesign = allDesigns.find(d => d.design_no === designNo && d.garment_part === 'TOP');
    const bottomDesign = allDesigns.find(d => d.design_no === designNo && d.garment_part === 'BOTTOM');
    
    setPreviewImage(toAbsoluteImageUrl(imageUrl));
    setSelectedPreviewDesignNo(designNo);
    setSelectedPreviewTopDesign(topDesign);
    setSelectedPreviewBottomDesign(bottomDesign);
    setPreviewDialogOpen(true);
  };

  // Add function to close the preview dialog
  const closeImagePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewImage(null);
    setSelectedPreviewDesignNo('');
    setSelectedPreviewTopDesign(undefined);
    setSelectedPreviewBottomDesign(undefined);
  };

  // Add formatDate function at the appropriate place in the file
  const formatDate = (dateString: string | Date): string => {
    try {
      if (!dateString) return 'N/A';
      
      // Handle both date-only strings and ISO date strings
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) return 'Invalid date';
      
      // Format as DD/MM/YYYY
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Error';
    }
  };

  // Define fallback columns that will work even if data is incomplete
  const getWorkerAssignmentColumns = () => {
    return [
      {
        field: 'id',
        headerName: 'ID',
        minWidth: 70,
        flex: 0.3,
      },
      {
        field: 'worker_id',
        headerName: 'Worker ID',
        minWidth: 100,
        flex: 0.5,
      },
      {
        field: 'worker_name',
        headerName: 'Worker Name',
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'design_image',
        headerName: 'Image',
        width: 70,
        renderCell: (params: SafeGridRenderCellParams) => {
          if (!params || !params.row) return <Typography>Error</Typography>;
          try {
            const imageUrl = params.row.image_url || params.row.design_cutting?.image_url || '';
            const absoluteUrl = toAbsoluteImageUrl(imageUrl);
            return absoluteUrl ? (
              <Box
                component="img"
                src={absoluteUrl}
                alt="Design"
                sx={{
                  width: 50,
                  height: 50,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                onClick={() => handleOpenDesignPreview(params.row.design_no, absoluteUrl)}
              />
            ) : (
              <Box 
                sx={{ 
                  width: 50, 
                  height: 50, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: '#f5f5f5',
                  color: '#999',
                  fontSize: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                onClick={() => handleOpenDesignPreview(params.row.design_no, '')}
              >
                No Image
              </Box>
            );
          } catch (error) {
            console.error('Error rendering design image:', error);
            return <Typography>Error</Typography>;
          }
        },
      },
      {
        field: 'design_no',
        headerName: 'Design',
        minWidth: 100,
        flex: 0.7,
      },
      {
        field: 'garment_part',
        headerName: 'Part',
        minWidth: 100,
        flex: 0.5,
      },
      {
        field: 'jobType',
        headerName: 'Job Type',
        minWidth: 120,
        flex: 0.7,
      },
      {
        field: 'assignment_type',
        headerName: 'Type',
        minWidth: 100,
        flex: 0.5,
        renderCell: (params: SafeGridRenderCellParams) => {
          if (!params || !params.row) return <Typography>Error</Typography>;
          const isRefinish = params.row.is_refinish_job;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isRefinish ? (
                <>
                  <BuildIcon color="warning" fontSize="small" />
                  <Typography variant="body2" color="warning.main">
                    Refinish
                  </Typography>
                </>
              ) : (
                <>
                  <CheckCircleIcon color="primary" fontSize="small" />
                  <Typography variant="body2" color="primary.main">
                    Regular
                  </Typography>
                </>
              )}
            </Box>
          );
        },
      },
      {
        field: 'size',
        headerName: 'Size',
        minWidth: 80,
        flex: 0.4,
        type: 'number',
      },
      {
        field: 'quantity_issued',
        headerName: 'Issued',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'total_received',
        headerName: 'Received',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'total_defects',
        headerName: 'Defects',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'stitching_rate',
        headerName: 'Rate',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'job_amount',
        headerName: 'Amount',
        minWidth: 100,
        flex: 0.7,
        type: 'number',
      },
      {
        field: 'issue_date',
        headerName: 'Issue Date',
        minWidth: 130,
        flex: 0.7,
      },
      {
        field: 'last_receive_date',
        headerName: 'Last Receive',
        minWidth: 130,
        flex: 0.7,
      },
      {
        field: 'remaining',
        headerName: 'Remaining',
        minWidth: 90,
        flex: 0.5,
        type: 'number',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        minWidth: 150,
        flex: 0.5,
        renderCell: (params: SafeGridRenderCellParams) => {
          try {
            const row = params.row as WorkerAssignment;
            // Calculate remaining safely by checking if properties exist
            const receivedQuantity = row.receives && row.receives.length > 0 
              ? row.receives.reduce((sum, receive) => sum + (receive.quantity_received || 0), 0) 
              : 0;
            
            const remaining = (row.quantity_issued || 0) - receivedQuantity;
            
            return (
              <Box display="flex" gap={1} alignItems="center">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleEdit(row)}
                  title="Edit"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                {remaining > 0 && (
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => handleReceive(row)}
                    title="Receive"
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                )}
                {!receivedQuantity && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(row.id)}
                    title="Delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            );
          } catch (error) {
            console.error('Error rendering actions:', error);
            return <Typography>Error</Typography>;
          }
        },
      },
    ] as GridColDef[];
  };

  // Use the function to define columns
  const columns: GridColDef[] = getWorkerAssignmentColumns();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [assignmentsRes, designsRes, workersRes] = await Promise.all([
        api.get('/worker-assignments'),
        api.get('/worker-assignments/available-designs'),
        api.get('/workers'),
      ]);

      if (!assignmentsRes.data || !designsRes.data || !workersRes.data) {
        throw new Error('Invalid response data from server');
      }

      // Transform the data to ensure all required properties exist
      const transformedAssignments = assignmentsRes.data.map((assignment: any) => {
        // Ensure worker object exists and has required properties
        const worker = assignment.worker || {};
        const designCutting = assignment.design_cutting || {};
        const receives = assignment.receives || [];

        // Calculate total received from receives array
        const totalReceived = receives.reduce(
          (sum: number, r: any) => sum + (Number(r.quantity_received) || 0) + (Number(r.defect_quantity) || 0),
          0
        );

        // Calculate defect total from receives array
        const totalDefects = receives.reduce(
          (sum: number, r: any) => sum + (Number(r.defect_quantity) || 0),
          0
        );

        // Get the last receive date if any receives
        const lastReceiveDate = receives.length > 0
          ? receives.sort((a: any, b: any) => {
              // Parse dates for comparison
              const dateA = new Date(a.receive_date).getTime();
              const dateB = new Date(b.receive_date).getTime();
              return dateB - dateA; // Sort descending (newest first)
            })[0].receive_date
          : null;

        // Ensure proper formatting of issue_date
        let formattedIssueDate = assignment.issue_date;
        try {
          if (assignment.issue_date) {
            // Preserve the original ISO format for consistent processing
            formattedIssueDate = assignment.issue_date;
          }
        } catch (error) {
          console.error('Error formatting issue_date:', error);
        }

        // Calculate job amount based on stitching rate and received quantity (excluding defects)
        const receivedQuantity = receives.reduce(
          (sum: number, r: any) => sum + (Number(r.quantity_received) || 0),
          0
        );
        const jobAmount = (assignment.stitching_rate || 0) * receivedQuantity;

        // Return a complete assignment object with fallbacks for missing data
        return {
          ...assignment,
          worker: {
            id: worker.id || 0,
            name: worker.name || 'Unknown',
            worker_id: worker.worker_id || 'Unknown'
          },
          design_cutting: {
            id: designCutting.id || 0,
            design_no: designCutting.design_no || assignment.design_no || '',
            garment_part: designCutting.garment_part || assignment.garment_part || '',
            image_url: designCutting.image_url || ''
          },
          image_url: designCutting.image_url || assignment.image_url || '',
          receives: receives.map((receive: any) => ({
            id: receive.id || 0,
            receive_date: receive.receive_date || '',
            quantity_received: receive.quantity_received || 0,
            defect_quantity: receive.defect_quantity || 0,
            notes: receive.notes || ''
          })),
          worker_name: worker.name || 'Unknown',
          total_received: totalReceived,
          total_defects: totalDefects,
          last_receive_date: lastReceiveDate,
          issue_date: formattedIssueDate,
          // Ensure jobType is explicitly available as a top-level field
          jobType: assignment.jobType || 'STITCHING',
          status: assignment.status || 'PENDING',
          stitching_rate: assignment.stitching_rate || 0,
          // Use the calculated job amount
          job_amount: jobAmount
        };
      });

      setAssignments(transformedAssignments);
      setFilteredAssignments(transformedAssignments);
      setAvailableDesigns(designsRes.data);
      
      const activeWorkersList = workersRes.data.filter((worker: ActiveWorker) => worker.status === 'ACTIVE');
      setAvailableWorkers(activeWorkersList);

      // Fetch all designs for preview functionality
      fetchAllDesigns();
    } catch (error: any) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setError(error.response?.data?.error || error.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = assignments;

    // Apply pending-only filter
    if (filterPendingOnly) {
      filtered = filtered.filter(assignment => assignment.status === 'PENDING');
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(assignment => 
        assignment.worker_id.toLowerCase().includes(query) ||
        assignment.worker.name.toLowerCase().includes(query) ||
        assignment.design_no.toLowerCase().includes(query)
      );
    }

    // Apply rate filter
    if (filterNoRate) {
      filtered = filtered.filter(assignment => 
        assignment.stitching_rate === null || assignment.stitching_rate === undefined || assignment.stitching_rate === 0
      );
    }

    // Apply delayed filter (more than 7 days old and still pending)
    if (filterDelayed) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      filtered = filtered.filter(assignment => {
        const issueDate = new Date(assignment.issue_date);
        return assignment.status === 'PENDING' && issueDate < sevenDaysAgo;
      });
    }

    setFilteredAssignments(filtered);
  }, [searchQuery, assignments, filterNoRate, filterDelayed, filterPendingOnly]);

  // Calculate pending quantities
  const getPendingSummary = () => {
    const summary = {
      stitchingIssued: 0,
      stitchingReceived: 0,
      pressIssued: 0,
      pressReceived: 0
    };

    assignments.forEach(assignment => {
      // Skip completed assignments
      if (assignment.status !== 'PENDING') return;

      const totalReceived = assignment.receives.reduce(
        (sum, r) => sum + r.quantity_received + r.defect_quantity, 
        0
      );

      if (assignment.jobType === 'STITCHING') {
        summary.stitchingIssued += assignment.quantity_issued;
        summary.stitchingReceived += totalReceived;
      } else if (assignment.jobType === 'PRESS') {
        summary.pressIssued += assignment.quantity_issued;
        summary.pressReceived += totalReceived;
      }
    });

    return summary;
  };

  const pendingSummary = getPendingSummary();
  const stitchingPending = pendingSummary.stitchingIssued - pendingSummary.stitchingReceived;
  const pressPending = pendingSummary.pressIssued - pendingSummary.pressReceived;
  const totalPending = stitchingPending + pressPending;

  const handleOpen = () => {
    setSelectedId(null);
    setFormData(initialFormData);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
    setFormErrors({});
    setError(null);
  };

  const handleReceiveClose = () => {
    setReceiveOpen(false);
    setReceiveFormData(initialReceiveFormData);
    setSelectedId(null);
    setReceiveFormErrors({});
    setError(null);
  };

  const handleReceive = (assignment: WorkerAssignment) => {
    setSelectedId(assignment.id);
    setSelectedAssignment(assignment);
    setReceiveFormData(initialReceiveFormData);
    setReceiveOpen(true);
  };

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantity' 
        ? Number(value)
        : value,
    }));
  };

  const handleReceiveInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setReceiveFormData(prev => ({
      ...prev,
      [name]: ['quantity_received', 'defect_quantity'].includes(name)
        ? value === '' ? '' : Number(value)
        : value,
    }));
  };

  const handleEdit = (assignment: WorkerAssignment) => {
    // Format the date from ISO to YYYY-MM-DD for the input field
    let formattedIssueDate = assignment.issue_date;
    try {
      if (assignment.issue_date) {
        const date = new Date(assignment.issue_date);
        formattedIssueDate = date.toISOString().split('T')[0];
      }
    } catch (error) {
      console.error('Error formatting issue_date for dialog:', error);
      formattedIssueDate = new Date().toISOString().split('T')[0];
    }

    setSelectedId(assignment.id);
    setFormData({
      workerId: assignment.worker_id,
      design_no: assignment.design_no,
      jobType: assignment.jobType,
      size: assignment.size,
      quantity: assignment.quantity_issued.toString(),
      issueDate: formattedIssueDate,
      stitching_rate: assignment.stitching_rate?.toString() || '',
      image_url: assignment.image_url || '',
      garment_part: assignment.garment_part,
    });
    setOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      setSuccess('');
      setFormErrors({});

      // Validate required fields
      const errors: {[key: string]: string} = {};
      if (!formData.workerId) errors.workerId = 'Worker is required';
      if (!formData.design_no) errors.design_no = 'Design is required';
      if (!formData.size) errors.size = 'Size is required';
      if (!formData.quantity) errors.quantity = 'Quantity is required';
      if (!formData.issueDate) errors.issueDate = 'Issue date is required';

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      const url = selectedId 
        ? `/worker-assignments/${selectedId}`
        : '/worker-assignments';
      
      const method = selectedId ? 'put' : 'post';

      // Find the selected design to get its garment_part
      const selectedDesign = availableDesigns.find(d => d.design_no === formData.design_no && d.garment_part === formData.garment_part);
      if (!selectedDesign) {
        setFormErrors({ design_no: 'Selected design not found' });
        return;
      }

      const submitData = {
        ...formData,
        workerId: parseInt(formData.workerId),
        design_no: formData.design_no,
        quantity: parseInt(formData.quantity),
        garment_part: selectedDesign.garment_part,
        stitching_rate: formData.stitching_rate ? parseFloat(formData.stitching_rate) : null,
        image_url: formData.image_url,
      };

      console.log('Submitting data:', submitData);

      await api[method](url, submitData);
      setSuccess(selectedId ? 'Assignment updated successfully' : 'Assignment created successfully');
      handleClose();
      fetchData();
    } catch (error: any) {
      console.error('Submit error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save assignment';
      
      if (errorMessage.toLowerCase().includes('quantity') || 
          errorMessage.toLowerCase().includes('pieces') ||
          errorMessage.toLowerCase().includes('issue')) {
        setFormErrors({ quantity: errorMessage });
      } else if (errorMessage.toLowerCase().includes('worker')) {
        setFormErrors({ workerId: errorMessage });
      } else if (errorMessage.toLowerCase().includes('design')) {
        setFormErrors({ design_no: errorMessage });
      } else if (errorMessage.toLowerCase().includes('size')) {
        setFormErrors({ size: errorMessage });
      } else if (errorMessage.toLowerCase().includes('rate')) {
        setFormErrors({ stitching_rate: errorMessage });
      } else {
        setError(errorMessage);
      }
    }
  };

  const handleReceiveSubmit = async () => {
    if (!selectedId) {
      console.error('No selectedId found');
      return;
    }

    try {
      setError(null);
      setReceiveFormErrors({});

      const assignment = assignments.find(a => a.id === selectedId);
      if (!assignment) {
        throw new Error('Assignment not found');
      }

      // Validate required fields
      const errors: {[key: string]: string} = {};
      if (!receiveFormData.receive_date) errors.receive_date = 'Receive date is required';
      if (!receiveFormData.quantity_received) errors.quantity_received = 'Quantity received is required';
      
      if (Object.keys(errors).length > 0) {
        setReceiveFormErrors(errors);
        return;
      }

      console.log('Submitting receive for assignment:', {
        id: assignment.id,
        worker_id: assignment.worker_id,
        design_no: assignment.design_no,
        quantity_issued: assignment.quantity_issued
      });

      // Calculate total received including this submission
      const totalReceived = assignment.receives.reduce((sum, r) => sum + r.quantity_received + r.defect_quantity, 0) +
        Number(receiveFormData.quantity_received) +
        (receiveFormData.defect_quantity === '' ? 0 : Number(receiveFormData.defect_quantity));

      if (totalReceived > assignment.quantity_issued) {
        setReceiveFormErrors({
          quantity_received: `Total received quantity cannot exceed issued quantity (${assignment.quantity_issued})`
        });
        return;
      }

      // Format the date as an ISO string
      const formattedDate = new Date(receiveFormData.receive_date).toISOString();

      const url = `/worker-assignments/${selectedId}/receives`;
      console.log('Making request to:', url);
      console.log('Request payload:', {
        receive_date: formattedDate,
        quantity_received: Number(receiveFormData.quantity_received),
        defect_quantity: receiveFormData.defect_quantity === '' ? 0 : Number(receiveFormData.defect_quantity),
        notes: receiveFormData.notes || ''
      });

      const response = await api.post(url, {
        receive_date: formattedDate,
        quantity_received: Number(receiveFormData.quantity_received),
        defect_quantity: receiveFormData.defect_quantity === '' ? 0 : Number(receiveFormData.defect_quantity),
        notes: receiveFormData.notes || ''
      });

      console.log('Response:', response.data);

      await fetchData();
      handleReceiveClose();
      setError(null);
      setSuccess('Assignment received successfully');
    } catch (error: any) {
      console.error('Full error details:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update assignment';
      
      // Check if error is related to quantity or other receive form fields
      if (errorMessage.toLowerCase().includes('quantity') || 
          errorMessage.toLowerCase().includes('received') ||
          errorMessage.includes('defect')) {
        setReceiveFormErrors({ quantity_received: errorMessage });
      } else if (errorMessage.toLowerCase().includes('date')) {
        setReceiveFormErrors({ receive_date: errorMessage });
      } else {
        setError(errorMessage);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      await api.delete(`/worker-assignments/${deleteId}`);
      await fetchData();
      setDeleteConfirmOpen(false);
      setDeleteId(null);
    } catch (error: any) {
      console.error('Error deleting assignment:', error);
      setError(error.response?.data?.error || 'Failed to delete assignment');
    }
  };

  const getAvailableSizes = (design: AvailableDesign) => {
    const sizes = [
      { key: 'xs_pieces', label: 'XS' },
      { key: 's_pieces', label: 'S' },
      { key: 'm_pieces', label: 'M' },
      { key: 'l_pieces', label: 'L' },
      { key: 'xl_pieces', label: 'XL' },
      { key: 'xxl_pieces', label: 'XXL' },
      { key: 'three_xl_pieces', label: '3XL' },
    ];

    return sizes
      .filter(size => {
        const value = design[size.key as keyof AvailableDesign];
        return typeof value === 'number' && value > 0;
      })
      .map(size => ({ label: size.label, key: size.key }));
  };

  const handleWorkerChange = (_event: any, value: ActiveWorker | null) => {
    setFormData(prev => ({
      ...prev,
      workerId: value?.id.toString() || ''
    }));
  };

  const handleRateEdit = (assignment: WorkerAssignment) => {
    setSelectedId(assignment.id);
    setRateFormData({
      stitching_rate: assignment.stitching_rate?.toString() || '',
    });
    setRateEditOpen(true);
  };

  const handleRateEditClose = () => {
    setRateEditOpen(false);
    setRateFormData({stitching_rate: ''});
    setSelectedId(null);
    setError(null);
    setSuccess('');
  };

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRateFormData({
      stitching_rate: e.target.value,
    });
  };

  const handleRateSubmit = async () => {
    if (!selectedId) return;

    try {
      setError(null);
      setSuccess('');

      const rateValue = rateFormData.stitching_rate === '' ? null : parseFloat(rateFormData.stitching_rate);
      
      if (rateFormData.stitching_rate !== '' && isNaN(rateValue as number)) {
        setError('Please enter a valid rate value');
        return;
      }

      await api.patch(`/worker-assignments/${selectedId}/rate`, {
        stitching_rate: rateValue,
      });

      setSuccess('Rate updated successfully');
      handleRateEditClose();
      fetchData();
    } catch (error: any) {
      console.error('Rate update error:', error);
      setError(error.response?.data?.error || 'Failed to update rate');
    }
  };

  const handleViewReceives = (assignment: WorkerAssignment) => {
    setSelectedAssignment(assignment);
    setReceivesListOpen(true);
  };

  // Helper function to get the size field key from a size label
  const getSizeKey = (size: string): string | null => {
    const sizeMap: Record<string, string> = {
      'XS': 'xs_pieces',
      'S': 's_pieces',
      'M': 'm_pieces',
      'L': 'l_pieces',
      'XL': 'xl_pieces',
      'XXL': 'xxl_pieces',
      '3XL': 'three_xl_pieces'
    };
    
    return sizeMap[size] || null;
  };

  const getAvailableQuantityMessage = (designNo: string, garmentPart: string, size: string, jobType: string) => {
    const selectedDesign = availableDesigns.find(d => d.design_no === designNo && d.garment_part === garmentPart);
    if (!selectedDesign) return "";
    
    const sizeKey = getSizeKey(size);
    if (!sizeKey) return "";
    
    // Safely access the value using the size key
    let availablePieces = 0;
    switch(sizeKey) {
      case 'xs_pieces':
        availablePieces = selectedDesign.xs_pieces;
        break;
      case 's_pieces':
        availablePieces = selectedDesign.s_pieces;
        break;
      case 'm_pieces':
        availablePieces = selectedDesign.m_pieces;
        break;
      case 'l_pieces':
        availablePieces = selectedDesign.l_pieces;
        break;
      case 'xl_pieces':
        availablePieces = selectedDesign.xl_pieces;
        break;
      case 'xxl_pieces':
        availablePieces = selectedDesign.xxl_pieces;
        break;
      case 'three_xl_pieces':
        availablePieces = selectedDesign.three_xl_pieces;
        break;
      default:
        availablePieces = 0;
    }
    
    if (jobType === "PRESS") {
      // For PRESS jobs, show total cut pieces without any restrictions
      return `Total cut: ${availablePieces} pieces`;
    } else {
      // For STITCHING jobs, calculate: Cut - Good Received - Outstanding - Shortfall
      const existingAssignments = assignments.filter(
        a => a.design_no === designNo && 
             a.garment_part === garmentPart && 
             a.size === size &&
             a.jobType === jobType
      );
      
      // Calculate good received (excluding defects)
      const goodReceived = existingAssignments.reduce((sum, a) => {
        const received = a.receives.reduce((rSum, r) => rSum + (r.quantity_received || 0), 0);
        return sum + received;
      }, 0);
      
      // Calculate outstanding (issued but not yet received)
      const totalIssued = existingAssignments.reduce((sum, a) => sum + a.quantity_issued, 0);
      const totalReceived = existingAssignments.reduce((sum, a) => {
        const received = a.receives.reduce((rSum, r) => rSum + (r.quantity_received || 0) + (r.defect_quantity || 0), 0);
        return sum + received;
      }, 0);
      const outstanding = totalIssued - totalReceived;
      
      // Note: Shortfall calculation would require API call, so we'll skip for now
      // Available = Cut - Good Received - Outstanding
      const availableQuantity = availablePieces - goodReceived - outstanding;
      return `Available: ${Math.max(0, availableQuantity)} pieces (Cut: ${availablePieces}, Good Received: ${goodReceived}, Outstanding: ${outstanding})`;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h4">Worker Assignments</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CheckCircleIcon />}
            onClick={() => setBulkReceiveOpen(true)}
          >
            Bulk Receive
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GroupAddIcon />}
            onClick={() => setBulkOpen(true)}
          >
            Bulk Assign
          </Button>
          <Button variant="contained" onClick={handleOpen}>
            New Assignment
          </Button>
        </Box>
      </Box>

      {/* Mobile Summary Toggle Button - Only visible on mobile */}
      <Box 
        sx={{ 
          display: { xs: 'flex', sm: 'none' }, 
          justifyContent: 'center', 
          mb: 2 
        }}
      >
        <Button 
          variant="outlined" 
          onClick={toggleSummary}
          fullWidth
          startIcon={summaryExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        >
          {summaryExpanded ? 'Hide Summary' : 'Show Summary'}
        </Button>
      </Box>

      {/* Score Card Section - Hidden on mobile unless expanded */}
      <Box 
        sx={{ 
          mb: 3, 
          display: { 
            xs: summaryExpanded ? 'block' : 'none', 
            sm: 'block' 
          } 
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>Pending Summary</Typography>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={12} sm={4}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 1.5, sm: 2 }, 
                bgcolor: 'primary.light', 
                color: 'primary.contrastText',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Total Pending
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                my: { xs: 0.5, sm: 1 },
                fontSize: { xs: '1.5rem', sm: '1.75rem' }
              }}>
                {totalPending} pcs
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: { xs: '0.7rem', sm: '0.75rem' } 
              }}>
                <Typography variant="caption">
                  Issued: {pendingSummary.stitchingIssued + pendingSummary.pressIssued}
                </Typography>
                <Typography variant="caption">
                  Received: {pendingSummary.stitchingReceived + pendingSummary.pressReceived}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 1.5, sm: 2 }, 
                bgcolor: 'info.light', 
                color: 'info.contrastText',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Stitching Pending
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                my: { xs: 0.5, sm: 1 },
                fontSize: { xs: '1.5rem', sm: '1.75rem' }
              }}>
                {stitchingPending} pcs
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: { xs: '0.7rem', sm: '0.75rem' } 
              }}>
                <Typography variant="caption">
                  Issued: {pendingSummary.stitchingIssued}
                </Typography>
                <Typography variant="caption">
                  Received: {pendingSummary.stitchingReceived}
                </Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 1.5, sm: 2 }, 
                bgcolor: 'success.light', 
                color: 'success.contrastText',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                Press Pending
              </Typography>
              <Typography variant="h5" sx={{ 
                fontWeight: 'bold', 
                my: { xs: 0.5, sm: 1 },
                fontSize: { xs: '1.5rem', sm: '1.75rem' }
              }}>
                {pressPending} pcs
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: { xs: '0.7rem', sm: '0.75rem' } 
              }}>
                <Typography variant="caption">
                  Issued: {pendingSummary.pressIssued}
                </Typography>
                <Typography variant="caption">
                  Received: {pendingSummary.pressReceived}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Search and Filter Section - Improved for mobile */}
      <Paper sx={{ p: 2, mb: 3 }}>
        {/* Search field - Always visible */}
        <Box sx={{ mb: { xs: 2, sm: 0 } }}>
          <TextField
            label="Search"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {/* Mobile Filter Button */}
        <Box sx={{ 
          display: { xs: 'flex', sm: 'none' }, 
          justifyContent: 'space-between',
          mt: 2
        }}>
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormGroup row sx={{ justifyContent: 'space-between', width: '100%' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={filterPendingOnly}
                    onChange={(e) => setFilterPendingOnly(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label="Pending Only"
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filterNoRate}
                    onChange={(e) => setFilterNoRate(e.target.checked)}
                    color="secondary"
                    size="small"
                  />
                }
                label="No Rate"
                sx={{ m: 0 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={filterDelayed}
                    onChange={(e) => setFilterDelayed(e.target.checked)}
                    color="warning"
                    size="small"
                  />
                }
                label="Delayed"
                sx={{ m: 0 }}
              />
            </FormGroup>
          </FormControl>
        </Box>
        
        {/* Desktop Filter Row */}
        <Stack 
          direction="row" 
          spacing={2} 
          alignItems="center"
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ display: { xs: 'none', sm: 'flex' }, mt: 2 }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={filterPendingOnly}
                onChange={(e) => setFilterPendingOnly(e.target.checked)}
                color="primary"
              />
            }
            label="Pending Only"
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterNoRate}
                onChange={(e) => setFilterNoRate(e.target.checked)}
                color="secondary"
              />
            }
            label="No Rate Only"
            sx={{ m: 0 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterDelayed}
                onChange={(e) => setFilterDelayed(e.target.checked)}
                color="warning"
              />
            }
            label="Delayed (>7 days)"
            sx={{ m: 0 }}
          />
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <DataGrid
        rows={filteredAssignments.map(assignment => ({
          ...assignment,
          worker_name: assignment.worker?.name || 'Unknown'
        })).filter(row => row && row.id)}
        columns={getWorkerAssignmentColumns()}
        getRowId={(row) => row?.id || 0}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        initialState={{
          sorting: {
            sortModel: [{ field: 'issue_date', sort: 'desc' }],
          },
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            padding: '8px 10px',
          }
        }}
      />

      {/* Assignment Form Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedId ? 'Edit Assignment' : 'New Assignment'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {Object.keys(formErrors).length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  Please correct the errors below
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={availableWorkers}
                getOptionLabel={(option) => `${option.name} (${option.worker_id})`}
                value={availableWorkers.find(w => w.id.toString() === formData.workerId) || null}
                onChange={handleWorkerChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Worker"
                    required
                    error={!!formErrors.workerId}
                    helperText={formErrors.workerId || ""}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.name} ({option.worker_id}) - {option.worker_type}
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={availableDesigns}
                getOptionLabel={(option) => `${option.design_no} - ${option.garment_part} (Batch ${option.batch_number})`}
                value={availableDesigns.find(d => d.design_no === formData.design_no && d.garment_part === formData.garment_part) || null}
                onChange={(_event, value) => {
                  setFormData(prev => ({
                    ...prev,
                    design_no: value?.design_no || '',
                    garment_part: value?.garment_part || '',
                    size: '',
                    quantity: '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Design"
                    required
                    error={!!formErrors.design_no}
                    helperText={formErrors.design_no || ""}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ display: 'flex', alignItems: 'center' }} {...props}>
                    {option.image_url && (
                      <Box
                        component="img"
                        src={toAbsoluteImageUrl(option.image_url)}
                        alt={option.design_no}
                        sx={{
                          width: 40, // Adjust size as needed
                          height: 40,
                          objectFit: 'cover',
                          mr: 1.5, // Margin right
                          borderRadius: '4px'
                        }}
                      />
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2">
                        {option.design_no} - {option.garment_part}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Batch {option.batch_number} • {option.total_pieces} pieces • {option.status}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.jobType}>
                <InputLabel>Job Type</InputLabel>
                <Select
                  name="jobType"
                  value={formData.jobType}
                  onChange={handleInputChange}
                  label="Job Type"
                >
                  <MenuItem value="STITCHING">Stitching</MenuItem>
                  <MenuItem value="PRESS">Press</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.size}>
                <InputLabel>Size</InputLabel>
                <Select
                  name="size"
                  value={formData.size}
                  onChange={handleInputChange}
                  label="Size"
                >
                  {formData.design_no && formData.garment_part && availableDesigns
                    .find(d => d.design_no === formData.design_no && d.garment_part === formData.garment_part)
                    && getAvailableSizes(availableDesigns.find(d => d.design_no === formData.design_no && d.garment_part === formData.garment_part)!)
                    .map(size => (
                      <MenuItem key={size.label} value={size.label}>
                        {size.label}
                      </MenuItem>
                    ))}
                </Select>
                {formErrors.size && <FormHelperText>{formErrors.size}</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                type="number"
                required
                error={!!formErrors.quantity}
                helperText={
                  formErrors.quantity ||
                  (formData.design_no && formData.garment_part && formData.size
                    ? getAvailableQuantityMessage(formData.design_no, formData.garment_part, formData.size, formData.jobType)
                    : "")
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Issue Date"
                name="issueDate"
                value={formData.issueDate}
                onChange={handleInputChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                required
                error={!!formErrors.issueDate}
                helperText={formErrors.issueDate || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rate"
                name="stitching_rate"
                value={formData.stitching_rate}
                onChange={handleInputChange}
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                error={!!formErrors.stitching_rate}
                helperText={formErrors.stitching_rate || ""}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive Form Dialog */}
      <Dialog open={receiveOpen} onClose={handleReceiveClose} maxWidth="sm" fullWidth>
        <DialogTitle>Receive Assignment</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {Object.keys(receiveFormErrors).length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please correct the errors below
              </Alert>
            )}
            <TextField
              fullWidth
              label="Receive Date"
              name="receive_date"
              type="date"
              value={receiveFormData.receive_date}
              onChange={handleReceiveInputChange}
              InputLabelProps={{ shrink: true }}
              required
              error={!!receiveFormErrors.receive_date}
              helperText={receiveFormErrors.receive_date || ""}
            />
            <TextField
              fullWidth
              label="Quantity Received"
              name="quantity_received"
              type="number"
              value={receiveFormData.quantity_received}
              onChange={handleReceiveInputChange}
              required
              error={!!receiveFormErrors.quantity_received}
              helperText={receiveFormErrors.quantity_received || ""}
            />
            <TextField
              fullWidth
              label="Defect Quantity"
              name="defect_quantity"
              type="number"
              value={receiveFormData.defect_quantity}
              onChange={handleReceiveInputChange}
              error={!!receiveFormErrors.defect_quantity}
              helperText={receiveFormErrors.defect_quantity || ""}
            />
            <TextField
              fullWidth
              label="Notes"
              name="notes"
              multiline
              rows={3}
              value={receiveFormData.notes}
              onChange={handleReceiveInputChange}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReceiveClose}>Cancel</Button>
          <Button onClick={handleReceiveSubmit} variant="contained">
            Receive
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rate Edit Dialog */}
      <Dialog open={rateEditOpen} onClose={handleRateEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Rate</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            <TextField
              fullWidth
              label="Rate"
              name="stitching_rate"
              type="number"
              value={rateFormData.stitching_rate}
              onChange={handleRateChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRateEditClose}>Cancel</Button>
          <Button onClick={handleRateSubmit} variant="contained">
            Update Rate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this assignment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receives List Dialog */}
      <ReceivesListDialog
        open={receivesListOpen}
        onClose={() => setReceivesListOpen(false)}
        assignment={selectedAssignment}
        onReceiveDeleted={fetchData}
      />

      {/* Add DesignPreviewDialog */}
      <DesignPreviewDialog
        open={previewDialogOpen}
        onClose={closeImagePreview}
        previewImage={previewImage}
        designNo={selectedPreviewDesignNo}
        topDesign={selectedPreviewTopDesign}
        bottomDesign={selectedPreviewBottomDesign}
      />

      {/* Bulk Assignment Dialog */}
      <BulkAssignmentDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSaved={() => { fetchData(); setSuccess('Assignments created successfully'); }}
        availableDesigns={availableDesigns}
        availableWorkers={availableWorkers}
      />

      {/* Bulk Receive Dialog */}
      <BulkReceiveDialog
        open={bulkReceiveOpen}
        onClose={() => setBulkReceiveOpen(false)}
        onSaved={() => { fetchData(); setSuccess('Bulk receive completed successfully'); }}
        assignments={assignments}
      />
    </Box>
  );
} 